const STANDARD_HTTPS_PORT = '443';
const STANDARD_HTTP_PORT = '80';

/** The tenant-less entry point of the app domain: `app.<domain>` carries no workspace. */
const ENTRY_SUBDOMAIN = 'app';

/** Non-default port from the current page when on the app domain (e.g. Vite dev on :3000). */
function currentDevPortSuffix(): string {
	if (!import.meta.env.DEV || typeof window === 'undefined') {
		return '';
	}

	const { port, hostname } = window.location;
	if (!hostname.endsWith(import.meta.env.VITE_APP_DOMAIN)) {
		return '';
	}

	if (!port || port === STANDARD_HTTPS_PORT || port === STANDARD_HTTP_PORT) {
		return '';
	}

	return `:${port}`;
}

/** Hostname for a tenant subdomain, including dev port when applicable. */
export function getTenantHost(subdomain: string): string {
	return `${subdomain}.${import.meta.env.VITE_APP_DOMAIN}${currentDevPortSuffix()}`;
}

/** Full HTTPS origin for a tenant subdomain. */
export function getTenantOrigin(subdomain: string): string {
	return `https://${getTenantHost(subdomain)}`;
}

/** Build a full tenant URL with path. */
export function buildTenantUrl(subdomain: string, pathname: string): string {
	const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
	return `${getTenantOrigin(subdomain)}${path}`;
}

export function buildAppUrl(pathname: string): string {
	return buildTenantUrl(ENTRY_SUBDOMAIN, pathname);
}

/**
 * Subdomain of the page currently loaded, or `null` when the page is served from a custom domain or
 * from the bare app domain. Reads `window.location` directly - the address bar is the source of
 * truth for which workspace the user asked for.
 */
function getCurrentSubdomain(): string | null {
	if (typeof window === 'undefined') {
		return null;
	}

	const { hostname } = window.location;

	if (!hostname.includes(import.meta.env.VITE_APP_DOMAIN)) {
		return null;
	}

	return hostname.replace(import.meta.env.VITE_APP_DOMAIN, '').replace('.', '') || null;
}

/**
 * Subdomain identifying a tenant workspace, i.e. the current subdomain minus the `app` entry point.
 * `null` means "the URL names no workspace", so the caller must fall back to the stored selection.
 */
export function getCurrentTenantSubdomain(): string | null {
	const subdomain = getCurrentSubdomain();

	return subdomain === ENTRY_SUBDOMAIN ? null : subdomain;
}

/** Whether the page is on the tenant-less `app.<domain>` entry point, which serves every workspace. */
export function isEntryHost(): boolean {
	return getCurrentSubdomain() === ENTRY_SUBDOMAIN;
}

/**
 * Whether an absolute URL points at this app domain or one of its subdomains, so a `?return=`
 * carried between hosts cannot bounce a session off-site.
 */
export function isAppDomainUrl(value: string | undefined): value is string {
	if (!value) return false;
	try {
		const url = new URL(value);
		const domain = import.meta.env.VITE_APP_DOMAIN;
		return url.protocol === 'https:' && (url.hostname === domain || url.hostname.endsWith(`.${domain}`));
	} catch {
		return false;
	}
}
